import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ensure cookies (access_token) are sent on every API call.
axios.defaults.withCredentials = true;

const AuthCtx = createContext({
  user: null,            // null = loading, false = not authed, object = authed
  login: async () => {},
  devLogin: async () => {},
  loginWithGoogleSession: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    // Skip the /me probe if we're returning from a Google OAuth flow — let the
    // AuthCallback exchange the session_id first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    sessionStorage.removeItem('raisey_dev_manual_logout');
    setUser(data);
    return data;
  };

  // DEV/PREVIEW ONLY — backend endpoint 404s in production (env-gated).
  const devLogin = async () => {
    const { data } = await axios.post(`${API}/auth/dev-login`);
    setUser(data);
    return data;
  };

  // Admin-only: switch this session into a real customer session (customer mode).
  const impersonate = async (customerId) => {
    const { data } = await axios.post(`${API}/auth/impersonate`, { customerId });
    setUser(data);
    return data;
  };

  const stopImpersonation = async () => {
    const { data } = await axios.post(`${API}/auth/impersonate/stop`);
    setUser(data);
    return data;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/google-session`, { session_id: sessionId });
    sessionStorage.removeItem('raisey_dev_manual_logout');
    setUser(data);
    return data;
  };

  const logout = async () => {
    // Remember the explicit sign-out so dev auto-login doesn't immediately
    // sign the user back in (lets testers switch accounts on preview).
    sessionStorage.setItem('raisey_dev_manual_logout', '1');
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Network/server hiccup on logout is non-fatal — the client always
      // clears local user state below so the UX still bails out to /login.
      console.warn('Logout API call failed (clearing client state anyway):', e?.message || e);
    }
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, login, devLogin, impersonate, stopImpersonation, loginWithGoogleSession, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);

// Convert any FastAPI error payload into a readable string for UI display.
export const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};
